/**
 * Drop-in replacements for Mantine hooks.
 * Import from here instead of @mantine/hooks.
 */
import { useState, useEffect, useRef, useCallback } from 'react';

/** useDisclosure — replaces @mantine/hooks useDisclosure */
export function useDisclosure(initial = false) {
  const [opened, setOpened] = useState(initial);
  const open    = useCallback(() => setOpened(true),  []);
  const close   = useCallback(() => setOpened(false), []);
  const toggle  = useCallback(() => setOpened(v => !v), []);
  return [opened, { open, close, toggle }];
}

/** useMediaQuery — replaces @mantine/hooks useMediaQuery */
export function useMediaQuery(query, initialValue = false) {
  const [matches, setMatches] = useState(() => {
    if (typeof window === 'undefined') return initialValue;
    return window.matchMedia(query).matches;
  });
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia(query);
    setMatches(mq.matches);
    const handler = (e) => setMatches(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [query]);
  return matches;
}

/** useDebouncedValue — replaces @mantine/hooks useDebouncedValue */
export function useDebouncedValue(value, wait) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), wait);
    return () => clearTimeout(t);
  }, [value, wait]);
  return [debounced];
}

/** useHotkeys — replaces @mantine/hooks useHotkeys */
export function useHotkeys(hotkeys) {
  useEffect(() => {
    const handler = (event) => {
      hotkeys.forEach(([hotkey, callback]) => {
        const parts  = hotkey.toLowerCase().split('+').map(p => p.trim());
        const key    = parts[parts.length - 1];
        const ctrl   = parts.includes('mod') || parts.includes('ctrl');
        const shift  = parts.includes('shift');
        const alt    = parts.includes('alt');
        const meta   = parts.includes('meta');
        const matchCtrl  = ctrl  ? (event.ctrlKey || event.metaKey) : (!event.ctrlKey && !event.metaKey);
        const matchShift = shift ? event.shiftKey : !event.shiftKey;
        const matchAlt   = alt   ? event.altKey   : !event.altKey;
        const matchKey   = event.key.toLowerCase() === key;
        if (matchCtrl && matchShift && matchAlt && matchKey) {
          event.preventDefault();
          callback(event);
        }
      });
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [hotkeys]);
}

/** useColorScheme — reads color scheme from Mantine's data attribute */
export function useColorScheme() {
  const [scheme, setScheme] = useState(() =>
    document.documentElement.getAttribute('data-mantine-color-scheme') || 'light'
  );
  useEffect(() => {
    const obs = new MutationObserver(() => {
      setScheme(document.documentElement.getAttribute('data-mantine-color-scheme') || 'light');
    });
    obs.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-mantine-color-scheme'],
    });
    return () => obs.disconnect();
  }, []);
  return scheme;
}

/** setColorScheme — writes color scheme to Mantine's data attribute */
export function setColorScheme(scheme) {
  document.documentElement.setAttribute('data-mantine-color-scheme', scheme);
  localStorage.setItem('mantine-color-scheme', scheme);
}

/**
 * useDragWheelScroll — smooth sliding for a horizontal overflow strip (filter thumbnails, tool rows…).
 * Returns a callback ref for the `overflow-x-auto` element:
 *   - a vertical mouse wheel scrolls the strip sideways, eased (trackpad side-swipes stay native; at
 *     either end the wheel goes back to scrolling the page);
 *   - a mouse can click-and-drag it, with a little momentum on release — a drag never also clicks
 *     the item it started on. Touch keeps the browser's own swipe.
 * Don't add CSS `scroll-behavior: smooth` to the strip: it would animate every frame's scrollLeft.
 */
export function useDragWheelScroll() {
  const cleanupRef = useRef(null);
  return useCallback((el) => {
    cleanupRef.current?.();
    cleanupRef.current = el ? attachDragWheelScroll(el) : null;
  }, []);
}

function attachDragWheelScroll(el) {
  let target = el.scrollLeft;
  let raf = 0;
  let expected = el.scrollLeft; // where the glide last put the strip
  const maxScroll = () => el.scrollWidth - el.clientWidth;
  const clamp = (v) => Math.max(0, Math.min(maxScroll(), v));
  // Ease scrollLeft toward `target`. A hidden document gets no animation frames, so jump there.
  const step = () => {
    // Something else scrolled the strip (a swipe, a keyboard focus jump) — let it win.
    if (Math.abs(el.scrollLeft - expected) > 1) { raf = 0; target = el.scrollLeft; return; }
    const d = target - el.scrollLeft;
    if (Math.abs(d) < 0.5) { el.scrollLeft = target; raf = 0; return; }
    el.scrollLeft += d * 0.25;
    expected = el.scrollLeft;
    raf = requestAnimationFrame(step);
  };
  const glide = () => {
    if (document.hidden) { el.scrollLeft = target; return; }
    expected = el.scrollLeft;
    if (!raf) raf = requestAnimationFrame(step);
  };
  const stop = () => { cancelAnimationFrame(raf); raf = 0; };

  const onWheel = (e) => {
    if (e.ctrlKey || maxScroll() <= 0 || Math.abs(e.deltaX) >= Math.abs(e.deltaY)) return;
    const from = raf ? target : el.scrollLeft;
    const delta = e.deltaY * (e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? el.clientWidth : 1);
    if ((delta < 0 && from <= 0) || (delta > 0 && from >= maxScroll())) return; // at an end → the page scrolls
    e.preventDefault();
    target = clamp(from + delta);
    glide();
  };

  let drag = null;
  let swallowClick = false;
  const onPointerDown = (e) => {
    if (e.pointerType !== 'mouse' || e.button !== 0 || maxScroll() <= 0) return;
    stop();
    drag = { id: e.pointerId, x: e.clientX, left: el.scrollLeft, lastX: e.clientX, lastT: e.timeStamp, v: 0, moved: false };
  };
  const onPointerMove = (e) => {
    if (!drag || e.pointerId !== drag.id) return;
    const dx = e.clientX - drag.x;
    if (!drag.moved) {
      if (Math.abs(dx) < 5) return; // a click, so far
      drag.moved = true;
      el.setPointerCapture?.(e.pointerId);
      el.style.cursor = 'grabbing';
      el.style.userSelect = 'none';
    }
    const dt = Math.max(1, e.timeStamp - drag.lastT);
    drag.v = (e.clientX - drag.lastX) / dt; // px per ms
    drag.lastX = e.clientX;
    drag.lastT = e.timeStamp;
    el.scrollLeft = clamp(drag.left - dx);
  };
  const onPointerUp = (e) => {
    if (!drag || e.pointerId !== drag.id) return;
    if (drag.moved) {
      swallowClick = true;
      el.style.cursor = '';
      el.style.userSelect = '';
      // Momentum: a flick glides on (at most one strip-width); a drag that paused before release stops dead.
      const v = e.timeStamp - drag.lastT > 80 ? 0 : drag.v;
      target = clamp(el.scrollLeft - Math.max(-el.clientWidth, Math.min(el.clientWidth, v * 180)));
      glide();
    }
    drag = null;
  };
  // The click that ends a drag lands on whatever item is under the mouse — eat it.
  const onClickCapture = (e) => {
    if (!swallowClick) return;
    swallowClick = false;
    e.preventDefault();
    e.stopPropagation();
  };

  el.addEventListener('wheel', onWheel, { passive: false });
  el.addEventListener('pointerdown', onPointerDown);
  el.addEventListener('pointermove', onPointerMove);
  el.addEventListener('pointerup', onPointerUp);
  el.addEventListener('pointercancel', onPointerUp);
  el.addEventListener('click', onClickCapture, true);
  return () => {
    stop();
    el.removeEventListener('wheel', onWheel);
    el.removeEventListener('pointerdown', onPointerDown);
    el.removeEventListener('pointermove', onPointerMove);
    el.removeEventListener('pointerup', onPointerUp);
    el.removeEventListener('pointercancel', onPointerUp);
    el.removeEventListener('click', onClickCapture, true);
  };
}
